
import { FuelConsumptionType, FuelUnit, SmartConnectVehicle, TripLog } from '@clients/myTrips';
import NumberInput from '@components/new-styleguide/form/NumberInput.vue';
import DurationInput from '@components/new-styleguide/form/DurationInput.vue';
import OeamtcSelect from '@components/new-styleguide/form/OeamtcSelect.vue';
import TooltipElement from '@components/new-styleguide/TooltipElement.vue';
import InformationLine from '@components/settings/information/InformationLine.vue';
import { LoadState } from '@util/types';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const TriplogModule = namespace('Triplog');

export type InformationType = 'serial' | 'odometer' | 'fuelConsumption' | 'setup' | 'fuelUnit' | 'fuelCapacity' | 'suggestions' | 'mergeBreakThreshold';

export interface InformationDetail {
  type: InformationType,
  name: string,
  value: string,
  unit?: string,
  link?: boolean,
  editable?: boolean,
  tooltip?: string,
  switch?: boolean
}

export interface InformationEdit {
  type: InformationType,
  editMode: boolean
}

@Component({
  name: 'InformationLines',
  components: {
    OeamtcSelect,
    TooltipElement,
    NumberInput,
    InformationLine,
    DurationInput,
  }
})
export default class InformationLines extends Vue {
  @Prop({ required: true })
  private list: InformationDetail[];

  @Prop({ default: false })
  private suggestionsDisabled: boolean;

  @TriplogModule.Getter('getSelectedTriplog')
  private selectedTriplog: TripLog;

  @TriplogModule.Action('fetchFitment')
  private fetchFitment;

  @TriplogModule.Action('setSeedOdometer')
  private setSeedOdometer;

  @TriplogModule.Action('setFuelConsumptionType')
  private setFuelConsumptionType;

  @TriplogModule.Action('setFuelUnit')
  private setFuelUnit;

  @TriplogModule.Action('setFuelCapacity')
  private setFuelCapacity;

  @TriplogModule.Action('setMergeBreakThreshold')
  private setMergeBreakThreshold;

  @TriplogModule.Getter('getSmartConnectVehicles')
  private smartConnectVehicles: Array<SmartConnectVehicle>;

  @TriplogModule.Getter('getSmartConnectVehiclesStatus')
  private smartConnectVehiclesStatus: LoadState;

  private listEdit: InformationEdit[] = [];

  private setupDisabled: boolean = false;
  private odometerDisabled: boolean = false;
  private fuelConsumptionDisabled: boolean = false;
  private fuelUnitDisabled: boolean = false;
  private fuelCapacityDisabled: boolean = false;
  private mergeBreakThresholdDisabled: boolean = false;
  private seedOdometer: number = null;
  private fuelConsumptionType: FuelConsumptionType = null;
  private fuelUnit: FuelUnit = null;
  private mergeBreakThreshold: string = null;
  private fuelCapacity: number = null;
  private errorText: string = null;

  private getDisabled(info: InformationDetail) {
    switch (info.type) {
      case 'setup': return this.setupDisabled;
      case 'odometer': return this.odometerDisabled;
      case 'fuelConsumption': return this.fuelConsumptionDisabled;
      case 'fuelUnit': return this.fuelUnitDisabled;
      case 'fuelCapacity': return this.fuelCapacityDisabled;
      case 'suggestions': return this.suggestionsDisabled;
      case "mergeBreakThreshold": return this.mergeBreakThresholdDisabled;
    }
  }

  private fuelTypeInput(value: number) {
    this.fuelConsumptionType = this.fuelConsumptionTypes.find(c => c.index === value).type;
  }

  private fuelUnitInput(value: number) {
    this.fuelUnit = this.fuelUnits.find(u => u.index === value).type;
  }

  get fuelConsumptionTypeIndex() {
    const fuelConsumptionType = this.fuelConsumptionTypes.find(c => c.type === this.fuelConsumptionType);
    if (!fuelConsumptionType) {
      return undefined;
    }
    return fuelConsumptionType.index;
  }

  get fuelUnitIndex() {
    const fuelUnit = this.fuelUnits.find(u => u.type === this.fuelUnit);
    if (!fuelUnit) {
      return undefined;
    }
    return fuelUnit.index;
  }

  get fuelUnitName() {
    return this.fuelUnits.find(u => u.type === this.selectedTriplog.vehicleSettings.fuelUnit).name;
  }

  get fuelUnits() {
    return [
      { name: 'Liter', type: FuelUnit.LITER, index: 0 },
      { name: 'kWh', type: FuelUnit.KWH, index: 1 },
      { name: 'kg', type: FuelUnit.KG, index: 2 }
    ];
  }

  get fuelConsumptionTypes() {
    return [
      { name: 'Gemessen', type: FuelConsumptionType.MEASURED, index: 0 },
      { name: 'Berechnet', type: FuelConsumptionType.CALCULATED, index: 1 }
    ]
  }

  get smartConnectVehiclesLoading() {
    return this.smartConnectVehiclesStatus === LoadState.Loading;
  }

  get smartConnectOdometer() {
    if (!this.smartConnectVehiclesLoading) {
      let smartConnect = this.smartConnectVehicles.find(o => o.vehicleId === this.selectedTriplog.vehicle.id);
      return smartConnect.odometer / 1000;
    }
    return null;
  }

  private getEditMode(info: InformationDetail) {
    const edit = this.listEdit.find(edit => edit.type === info.type);
    return edit ? edit.editMode : false;
  }

  private async link(info: InformationDetail) {
    if (info.type === 'setup') {
      this.setupDisabled = true;
      await this.fetchFitment({ vehicle: this.selectedTriplog.vehicle })
        .catch(() => this.fetchFitment({ vehicle: this.selectedTriplog.vehicle }));
      await this.$router.push({ name: 'installationGuide' });
      this.setupDisabled = false;
    }
  }

  private editMode(info: InformationDetail) {
    if (info.type === 'odometer') {
      this.seedOdometer = this.smartConnectOdometer || 0;
    } else if (info.type === 'fuelConsumption') {
      this.fuelConsumptionType = this.fuelConsumptionTypes.find(c => c.name === info.value).type;
    } else if (info.type === 'fuelUnit') {
      this.fuelUnit = this.fuelUnits.find(u => u.name === info.value).type;
    } else if (info.type === 'fuelCapacity') {
      this.fuelCapacity = this.selectedTriplog.vehicleSettings.fuelCapacity || 0;
    }
    this.writeListEdit(info, true);
  }

  private async editSubmit(info: InformationDetail) {
    if (info.type === 'odometer') {
      this.odometerDisabled = true;

      try {
        await this.setSeedOdometer({
          vehicleId: this.selectedTriplog.vehicle.id,
          seedOdometer: this.seedOdometer * 1000
        });
      } catch (e) {
        this.errorText = 'Es ist ein Fehler beim Ändern vom Kilometerstand aufgetreten.';
      } finally {
        this.odometerDisabled = false;
      }
    } else if (info.type === 'fuelConsumption') {
      this.fuelConsumptionDisabled = true;

      try {
        await this.setFuelConsumptionType({
          vehicleId: this.selectedTriplog.vehicle.id,
          fuelConsumptionType: this.fuelConsumptionType
        });
      } catch (e) {
        this.errorText = 'Es ist ein Fehler beim Ändern der Verbrauchswerte aufgetreten.';
      } finally {
        this.fuelConsumptionDisabled = false;
      }
    } else if (info.type === 'fuelUnit') {
      this.fuelUnitDisabled = true;

      try {
        await this.setFuelUnit({
          vehicleId: this.selectedTriplog.vehicle.id,
          fuelUnit: this.fuelUnit
        });
      } catch (e) {
        this.errorText = 'Es ist ein Fehler beim Ändern der Einheit des Verbrauchs aufgetreten.';
      } finally {
        this.fuelUnitDisabled = false;
      }
    }
    else if (info.type === 'fuelCapacity') {
      this.fuelCapacityDisabled = true;

      try {
        await this.setFuelCapacity({
          vehicleId: this.selectedTriplog.vehicle.id,
          fuelCapacity: this.fuelCapacity
        });
      } catch (e) {
        this.errorText = 'Es ist ein Fehler beim Ändern des Tankvolumens aufgetreten.';
      } finally {
        this.fuelCapacityDisabled = false;
      }
    } else if (info.type === 'mergeBreakThreshold') {
      this.mergeBreakThresholdDisabled = true;

      try {
        await this.setMergeBreakThreshold({
          vehicleId: this.selectedTriplog.vehicle.id,
          mergeBreakThreshold: this.mergeBreakThreshold
        });
      } catch (e) {
        console.log(e)
        this.errorText = 'Es ist ein Fehler beim Ändern der Merge-Pausen aufgetreten.';
      } finally {
        this.mergeBreakThresholdDisabled = false;
      }
    }
    this.writeListEdit(info, false);
  }

  private editCancel(info: InformationDetail) {
    this.writeListEdit(info, false);
  }

  private writeListEdit(info: InformationDetail, editMode: boolean) {
    const edit = this.listEdit.find(edit => edit.type === info.type);
    if (!edit) {
      this.listEdit.push({ type: info.type, editMode })
    } else {
      edit.editMode = editMode;
    }
  }
}
